import React, { useState, useEffect } from 'react';
import { Drawer } from 'antd';
import styles from './LeagueInfo.module.css';
import matchPlanningButton from '../../assets/images/league/match_planning_button.png';
import { fetchActivityCreditHours } from '../../api/api';

// ─── Drawer content ───────────────────────────────────────────────────────────

const GroupsContent = () => (
  <div className={styles.drawerContent}>
    <p>
      Wszyscy uczestnicy ligi są podzieleni na grupy A, B, C… według poziomu gry.
      W każdej grupie jest 4–5 zawodników o zbliżonych umiejętnościach — mecze są
      wyrównane i emocjonujące.
    </p>

    <h4 className={styles.subheading}>Grupy i ich wagi</h4>
    <table className={styles.table}>
      <thead>
        <tr><th>Grupa</th><th>Premia bazowa</th></tr>
      </thead>
      <tbody>
        <tr><td>A (najwyższa)</td><td>5 pkt</td></tr>
        <tr><td>B</td><td>4 pkt</td></tr>
        <tr><td>C</td><td>3 pkt</td></tr>
        <tr><td>D</td><td>2 pkt</td></tr>
        <tr><td>E (wejściowa)</td><td>1 pkt</td></tr>
      </tbody>
    </table>

    <h4 className={styles.subheading}>Awanse i spadki</h4>
    <p>
      Po każdej rundzie zawodnicy z najwyższymi wynikami awansują do grupy wyżej,
      a najsłabsi schodzą niżej. Liczba awansujących i spadających może się zmieniać
      w trakcie sezonu — na początku ligi większa rotacja pomaga skalibrować grupy
      (dopasować graczy do właściwego poziomu), a z czasem może być zmniejszona
      do 1 osoby, gdy skład grup się ustabilizuje.
    </p>

    <div className={styles.example}>
      <strong>Przykład:</strong> W grupie B gra 5 zawodników. Po rundzie: 1. i 2. miejsce
      awansuje do grupy A, 4. i 5. miejsce spada do grupy C. 3. miejsce pozostaje w B.
    </div>
  </div>
);

const ScoringContent = () => (
  <div className={styles.drawerContent}>
    <p>
      W lidze BTT obowiązuje system punktacji <strong>3-2-1-0</strong>.
      Nagradzana jest zarówno wygrana, jak i samo podjęcie walki — nawet po przegranej
      w wyrównanym meczu zdobywasz punkt.
    </p>

    <h4 className={styles.subheading}>Punkty za mecz</h4>
    <table className={styles.table}>
      <thead>
        <tr><th>Wynik meczu</th><th>Zwycięzca</th><th>Rywal</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Wygrana dominująca (2:0 w setach)</td>
          <td><strong>3 pkt</strong></td>
          <td>0 pkt</td>
        </tr>
        <tr>
          <td>Wygrana wyrównana (2:1 w setach)</td>
          <td><strong>2 pkt</strong></td>
          <td><strong>1 pkt</strong></td>
        </tr>
        <tr>
          <td>Mecz nierozegrany</td>
          <td>0 pkt</td>
          <td>0 pkt</td>
        </tr>
      </tbody>
    </table>
    <p>
      Za każdy <strong>rozegrany mecz</strong> — niezależnie od wyniku — obaj gracze
      otrzymują dodatkowo <strong>+0,3 pkt</strong> jako bonus za aktywność.
    </p>
    <p>
      Do rankingu głównego trafia nie suma surowych punktów, lecz
      ich <strong>średnia na mecz</strong> (suma punktów w rundzie / liczba zaplanowanych meczów).
      Dzięki temu wyniki są porównywalne między grupami różnej wielkości.
      Nierozegrany mecz daje 0 punktów, ale nadal wchodzi do mianownika —
      każdy opuszczony mecz <strong>obniża średnią</strong> i tym samym wynik w rankingu głównym.
    </p>

    <h4 className={styles.subheading}>Premia grupowa</h4>
    <p>
      Oprócz punktów za wyniki dostajesz premię grupową — zależną od grupy, w której grasz.
      Im wyższa grupa, tym wyższa premia. Premia jest jednak <strong>proporcjonalna do liczby
      rozegranych meczów</strong> — za każdy nierozegrany mecz tracisz część premii grupowej.
      Grasz wszystko: dostajesz pełną premię. Nie grasz połowy: dostajesz połowę premii.
    </p>

    <h4 className={styles.subheading}>Bonus top 3</h4>
    <p>
      Trzej najlepsi zawodnicy w grupie po każdej rundzie otrzymują procentowy bonus
      doliczany do sumy średniej punktów za mecz i premii grupowej:
    </p>
    <table className={styles.table}>
      <thead>
        <tr><th>Miejsce w grupie</th><th>Bonus</th></tr>
      </thead>
      <tbody>
        <tr><td>1. miejsce</td><td><strong>+15%</strong></td></tr>
        <tr><td>2. miejsce</td><td><strong>+10%</strong></td></tr>
        <tr><td>3. miejsce</td><td><strong>+5%</strong></td></tr>
        <tr><td>4. miejsce i niżej</td><td>bez bonusu</td></tr>
      </tbody>
    </table>

    <div className={styles.example}>
      <strong>Przykład (grupa A, 4 zaplanowane mecze):</strong><br />
      Jan rozegrał 3 z 4 meczów. Wygrał 2 dominująco (2:0) i 1 wyrównanie (2:1), 1 mecz nierozegrany.<br />
      • Punkty meczowe: 2×3 + 1×2 = 8 pkt<br />
      • Bonus za rozegranie (3 mecze × 0,3): 0,9 pkt<br />
      • Suma: 8 + 0,9 = 8,9 pkt<br />
      • Średnia na mecz: 8,9 / 4 = 2,225 pkt<br />
      • Premia grupowa: 5 pkt × (3/4) = 3,75 pkt<br />
      • Baza: 2,225 + 3,75 = 5,975 pkt<br />
      • Jan skończył na 1. miejscu → bonus +15%: 5,975 × 0,15 = 0,90 pkt<br />
      • Razem za rundę: <strong>6,87 pkt</strong>
    </div>
  </div>
);

const RankingContent = () => (
  <div className={styles.drawerContent}>
    <p>
      Ranking ogólny zmienia się dynamicznie — aktualizuje się na bieżąco
      z każdym wprowadzonym wynikiem meczu.
    </p>
    <p>
      Wynik każdej rundy to suma: średniej liczby punktów za mecz, premii grupowej
      i ewentualnego bonusu top 3. Punkty ze wszystkich rund sumują się do rankingu ogólnego.
      Szczegóły punktacji opisane są w zakładce <strong>System punktacji</strong>.
    </p>

    <h4 className={styles.subheading}>Procent rozegranych meczów</h4>
    <p>
      Klikając w wartość w kolumnie <strong>Pkt</strong>, otworzysz szczegóły punktacji —
      u góry zobaczysz procent w rodzaju „79% rozegranych meczów (15/19)".
      Sama kolumna <strong>M</strong> tego nie pokazuje: 3 rozegrane mecze mogą być kompletem
      w małej grupie albo dopiero połową obowiązków w dużej — procent mierzy właśnie to drugie.
    </p>
    <ul className={styles.list}>
      <li>Mianownikiem jest liczba meczów wygenerowanych dla gracza w każdej rundzie.</li>
      <li>
        <strong>Walkower wygrany</strong> (rywal się nie stawił) <strong>liczy się</strong> jako
        mecz zaliczony — gracz wywiązał się ze swojego obowiązku i nie może tracić za to,
        że przeciwnik nie przyszedł. <strong>Walkower oddany nie liczy się.</strong>
      </li>
      <li><strong>Krecz lub kontuzja liczy się</strong> — mecz był rozgrywany.</li>
      <li>Rundy zamrożone oraz rundy sprzed dołączenia gracza do ligi nie wchodzą do statystyki.</li>
    </ul>
    <p className={styles.note}>
      Z tego powodu procent bywa liczony z <strong>większej</strong> liczby meczów niż widać
      w kolumnie <strong>M</strong> — ona liczy tylko mecze rozegrane do końca i pomija walkowery
      po obu stronach. To nie błąd: <strong>M</strong> pokazuje, ile zagrałeś, a procent — ile ze
      swoich obowiązków wypełniłeś. Trwająca runda też wchodzi do statystyki, więc tuż po jej
      starcie procent chwilowo spada wszystkim i rośnie w miarę rozgrywania kolejnych meczów —
      dzięki temu statystyka pokazuje, kto jest w tej chwili do tyłu z graniem.
    </p>

    <h4 className={styles.subheading}>Awans do fazy playoff</h4>
    <p>
      Po ostatniej rundzie zasadniczej <strong>12 najlepszych graczy</strong> rankingu ogólnego
      awansuje do <strong>fazy playoff</strong>. Miejsca premiowane awansem są wyróżnione
      w tabeli rankingu jasnym bursztynowym tłem z ciemniejszym paskiem przy lewej krawędzi —
      dzięki temu przez cały sezon widać, kto jest w strefie playoff, a kto tuż za nią.
    </p>

    <h4 className={styles.subheading}>Rozstrzyganie remisów w rankingu ogólnym</h4>
    <p>
      Gdy dwóch lub więcej graczy ma tę samą liczbę punktów, o kolejności decydują
      poniższe kryteria stosowane parami — po kolei, aż do rozstrzygnięcia:
    </p>
    <ol className={styles.list}>
      <li><strong>Zwycięstwa H2H</strong> — kto wygrał więcej bezpośrednich meczów w całym sezonie?</li>
      <li><strong>Bilans setów H2H</strong> — bilans setów z bezpośrednich meczów w całym sezonie</li>
      <li><strong>Łączna liczba wygranych</strong> — ze wszystkich rund sezonu</li>
      <li><strong>Bilans wygranych/przegranych</strong> — różnica między wygraniami a przegranymi</li>
      <li><strong>Więcej rozegranych meczów</strong> — kto zagrał więcej meczów w sezonie</li>
    </ol>
    <p className={styles.note}>
      Przy 3 lub więcej graczach z tą samą liczbą punktów kryteria stosowane są parami —
      bez osobnej mini-ligi.
    </p>
  </div>
);

const ActivityContent = ({ activityHours = 36 }) => (
  <div className={styles.drawerContent}>
    <p>
      Punkty za aktywność decydują o tym, kto wygrywa walkowerem nierozegrany mecz.
      Jeśli po zakończeniu rundy mecz nie został rozegrany, system automatycznie
      podczas generowania następnej rundy przyznaje wygraną <strong>6:0, 6:0</strong> graczowi
      z <strong>większą liczbą punktów aktywności</strong>. Gracz mniej aktywny przegrywa walkowerem.
    </p>
    <p>
      Oznacza to, że przy nierozegranych meczach gracz z małą liczbą punktów aktywności
      jest w niekorzystnej sytuacji — może stracić punkty rankingowe, na które miał szansę.
      Jeśli obaj gracze mają <strong>tyle samo punktów aktywności</strong>, mecz pozostaje
      nierozegrany — obaj tracą proporcjonalnie poprzez obniżoną średnią i premię grupową.
    </p>

    <p>Punktacja aktywności składa się z dwóch niezależnych systemów sumowanych ze sobą.</p>

    <h4 className={styles.subheading}>Propozycje (maks. 4 pkt/tydzień)</h4>
    <table className={styles.table}>
      <thead>
        <tr><th>Akcja</th><th>Punkty</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Oferta, której okno kończy się <strong>przed 19:00</strong> (nieodwołana)</td>
          <td><strong>1 pkt</strong> za każdą, ale łącznie maks. <strong>2 pkt</strong></td>
        </tr>
        <tr>
          <td>Oferta, której okno kończy się <strong>o 19:00 lub później</strong> (nieodwołana) — czyli obejmująca wieczór</td>
          <td><strong>1 pkt</strong> za każdą</td>
        </tr>
      </tbody>
    </table>
    <p>
      Decyduje <strong>koniec okna oferty</strong>, nie jego początek — a samo okno może być dłuższe
      niż wymagane minimum 2 godzin. Aby zdobyć pełne <strong>4 pkt</strong> za propozycje, co najmniej{' '}
      <strong>2 pkt muszą pochodzić z ofert, których okno kończy się o 19:00 lub później</strong>{' '}
      (oferty kończące się przed 19:00 dają łącznie maks. 2 pkt).
      Przykłady: 1 oferta dzienna → 1 pkt; 10 dziennych → 2 pkt; 10 dziennych + 1 wieczorna → 3 pkt;
      2 dzienne + 2 wieczorne → 4 pkt; 10 wieczornych → 4 pkt.
    </p>
    <p>
      Do punktacji liczą się oferty <strong>„wygasłe"</strong> (nikt ich nie przyjął), a{' '}
      <strong>nie</strong> te <strong>„anulowane"</strong>. Gdy w oknie oferty{' '}
      <strong>wszystkie korty są zajęte</strong>, system anuluje ją automatycznie — ale jeśli
      czekała <strong>co najmniej {activityHours} godzin</strong>, dostaje status <strong>„wygasła"</strong>{' '}
      i <strong>liczy się</strong> do aktywności (zrobiłeś swoje — brak wolnego kortu nie jest
      Twoją winą). Jeśli czekała krócej niż {activityHours} h — zostaje <strong>„anulowana"</strong> i nie
      liczy się. Ofertę odwołaną przez Ciebie samego zawsze oznaczamy jako{' '}
      <strong>„anulowana"</strong> (bez punktu).
    </p>
    <p>
      Do punktacji propozycji wliczają się wyłącznie oferty <strong>których mecz nie został jeszcze rozegrany</strong>.
      Jeśli wystawiłeś ofertę, rywal ją przyjął i mecz się odbył — ta oferta <em>nie</em> liczy się
      do punktów za propozycje. Punkt dostaniesz za sam rozegrany mecz (patrz niżej).
      Dzięki temu za jedno spotkanie nie można dostać punktu podwójnie.
    </p>
    <div className={styles.example}>
      <strong>Przykład:</strong> Wystawiłeś ofertę na środę w oknie 17:30–19:30 (okno kończy się o 19:30,
      czyli o 19:00 lub później). Rywal przyjął, mecz rozegrany, wynik wpisany.<br />
      → Oferta <em>nie wchodzi</em> do puli propozycji (0 pkt za propozycje z tego tytułu).<br />
      → Mecz daje <strong>+1 pkt meczowy</strong>.<br />
      Łącznie za tę aktywność: <strong>1 pkt</strong> (nie 2).
    </div>

    <h4 className={styles.subheading}>Rozegrane mecze (bez limitu)</h4>
    <table className={styles.table}>
      <thead>
        <tr><th>Akcja</th><th>Punkty</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Rozegrany mecz (wynik wpisany, nie walkower)</td>
          <td><strong>+1 pkt</strong> za każdy</td>
        </tr>
      </tbody>
    </table>
    <p>Punkty za mecze nie mają górnego limitu — 3 mecze w tygodniu = 3 dodatkowe punkty.</p>
    <p><strong>Sprawdzanie aktywności:</strong> we wtorek oczekujemy co najmniej <strong>2 pkt</strong>, a w czwartek <strong>4 pkt</strong> — poniżej progu wysyłamy przypomnienie, a przy nierozegranych meczach gracz naraża się na walkower. Maks. aktywność w rundzie zależy też od liczby rozegranych meczów (bez limitu).</p>

    <p>
      Aktualną liczbę punktów aktywności możesz sprawdzić w formularzu{' '}
      <strong>„Planowanie meczów"</strong> dostępnym w widoku ligi.
    </p>

    <h4 className={styles.subheading}>Kiedy system wysyła przypomnienia?</h4>
    <p>
      We <strong>wtorek i czwartek o godz. 11:00</strong> system sprawdza aktywność bieżącego tygodnia.
      Jeśli masz nierozegrane mecze i jesteś poniżej progu na dany dzień — we <strong>wtorek mniej niż 2 pkt</strong>,
      a w <strong>czwartek mniej niż 4 pkt</strong> — otrzymasz e-mail z przypomnieniem.
    </p>

    <div className={styles.example}>
      <strong>Zalecana praktyka:</strong> złóż kilka propozycji na nadchodzący tydzień
      w dogodnym dla ciebie czasie. Po zaakceptowaniu jednej z ofert system automatycznie
      anuluje pozostałe twoje oferty na ten sam dzień — nie musisz robić tego ręcznie.
    </div>

    <p className={styles.note}>
      Jeśli w danej rundzie byłeś zamrożony —
      aktywność za tę rundę nie jest liczona.
    </p>
  </div>
);

const MatchPlanningContent = () => (
  <div className={styles.drawerContent}>
    <p>
      Zachęcamy do umawiania wszystkich spotkań przy pomocy formularza
      {' '}<strong>„Planowanie meczów"</strong> dostępnego w widoku ligi.
    </p>

    <figure className={styles.figure}>
      <img
        src={matchPlanningButton}
        alt="Przycisk Planowanie meczów w widoku ligi"
        className={`${styles.figureImage} ${styles.figureImageSmallDesktop}`}
      />
      <figcaption className={styles.figureCaption}>
        Przycisk znajdziesz w widoku swojej ligi, nad tabelą grup —
        widoczny tylko dla zalogowanych uczestników rozgrywek.
      </figcaption>
    </figure>

    <h4 className={styles.subheading}>Okno czasowe zamiast jednej godziny</h4>
    <p>
      Propozycja zawiera <strong>okno dostępności</strong> — godzinę od i do (np. 16:00–20:00).
      Oznacza to: „jestem wolny w tym przedziale".
      Przyjmujący ofertę wybiera konkretny dwugodzinny slot przy pomocy suwaka
      oraz <strong>numer wolnego kortu</strong> — system rezerwuje go automatycznie.
    </p>
    <p className={styles.note}>
      Okno musi trwać co najmniej 2 godziny i kończyć się najpóźniej o 22:00.
      Jeśli okno wynosi dokładnie 2 godziny, suwak nie pojawia się — jest tylko jeden możliwy termin.
    </p>
    <p className={styles.note}>
      W jednym dniu możesz mieć maksymalnie <strong>2 aktywne oferty</strong> (oferty przyjęte, wygasłe
      i anulowane nie zajmują miejsca w limicie). Twoje oferty tego samego dnia <strong>nie mogą się
      pokrywać ani stykać</strong> — okna 07:00–11:00 i 08:00–12:00 wykluczają się, podobnie jak
      07:00–09:00 i 09:00–11:00. Wystaw jedno szersze okno zamiast kilku zachodzących na siebie.
    </p>
    <p className={styles.note}>
      Nowych propozycji <strong>nie można składać w ostatnim dniu rundy</strong> — w tym dniu jest już
      za późno na planowanie nowych meczów. Wcześniej wystawione propozycje przypadające na ten dzień
      można w ostatnim dniu normalnie <strong>przyjmować i rozgrywać</strong>.
    </p>

    <h4 className={styles.subheading}>Co daje planowanie w systemie?</h4>
    <ul className={styles.list}>
      <li>
        <strong>Przejrzystość w grupie</strong> — wszyscy widzą aktywność innych,
        mogą reagować na wystawione propozycje. Koniec z ciągłym pytaniem
        „czy ktoś dzisiaj zagra?" — każdy wystawia okno, które mu odpowiada,
        i czeka na akceptację. Im więcej propozycji w tygodniu, tym większa szansa na mecz.
      </li>
      <li>
        <strong>Automatyczna rezerwacja kortu</strong> — po wyborze slotu i kortu przez akceptującego
        system tworzy rezerwację oznaczoną jako LIG (mecz ligowy),
        która <strong>nie jest objęta opłatą za kort</strong>.
        Ma to szczególne znaczenie dla graczy niebędących członkami BTT,
        którzy normalnie nie są zwolnieni z opłat za rezerwacje.
      </li>
      <li>
        <strong>Aktywne klucze dostępowe dla obu graczy</strong> — w zakresie od 10 minut
        przed do 10 minut po zarezerwowanym kortem na mecz ligowy obaj zawodnicy mają
        aktywne kody dostępu do pomieszczenia klubowego, niezależnie od tego, na czyje
        nazwisko jest wystawiona rezerwacja.
      </li>
    </ul>

    <h4 className={styles.subheading}>Powiadomienia e-mail</h4>
    <ul className={styles.list}>
      <li>
        <strong>Nowe propozycje</strong> — dwa razy dziennie (13:00 i 20:00) system wysyła
        jeden zbiorczy e-mail do graczy w grupie, którzy nie rozegrali jeszcze meczu
        z danym zawodnikiem i nie mają z nim umówionego terminu.
        W jednej wiadomości mogą pojawić się oferty od kilku graczy.
      </li>
      <li>
        <strong>Przyjęcie propozycji</strong> — obaj gracze otrzymują natychmiastowe
        potwierdzenie z wybranym terminem i miejscem meczu.
      </li>
      <li>
        <strong>Anulowanie umówionego meczu</strong> — obaj gracze otrzymują natychmiastową
        wiadomość z podanym powodem anulowania.
      </li>
      <li>
        <strong>Brak wolnych kortów</strong> — dwa razy dziennie (12:00 i 19:00) system
        sprawdza aktywne propozycje. Jeśli w żadnym dwugodzinnym slocie w oknie propozycji
        nie ma już wolnego kortu, propozycja zostaje anulowana. Autor otrzymuje jeden zbiorczy
        e-mail, nawet jeśli anulowano kilka jego ofert jednocześnie.
        Ten sam warunek jest sprawdzany w momencie wystawiania oferty — system nie przyjmie
        propozycji, gdy żaden slot w oknie nie ma wolnego kortu.
      </li>
      <li>
        <strong>Brak kortów przy akceptacji</strong> — jeśli w chwili przyjmowania oferty
        wszystkie korty są już zajęte, oferta zostaje natychmiast anulowana,
        a wystawca otrzymuje powiadomienie e-mail z informacją kto próbował ją przyjąć.
      </li>
      <li>
        <strong>Wygaśnięcie propozycji</strong> — oferta wygasa automatycznie (bez e-maila),
        gdy minął ostatni możliwy slot w oknie, tj. gdy godzina <em>zakończenia okna</em> minus
        2 godziny już upłynęła.
      </li>
      <li>
        <strong>Anulowanie nieprzyjętej oferty</strong> — anulowanie własnej propozycji,
        która nie została jeszcze zaakceptowana, nie powoduje wysyłania żadnych powiadomień.
      </li>
    </ul>

    <h4 className={styles.subheading}>Czat grupowy WhatsApp</h4>
    <p>
      Każda grupa ligowa (A, B, C, D, …) ma dedykowany czat WhatsApp.
      Czat jest <strong>uzupełnieniem</strong> formalnego planowania meczów w aplikacji
      (do którego gorąco zachęcamy) — przydaje się w sytuacjach, gdy potrzebna jest
      <strong> szybka reakcja</strong>, np. nagłe okienko w grafiku lub odwołany trening.
    </p>
    <p className={styles.note}>
      Nie zachęcamy, żeby czat stał się <strong>stałą praktyką</strong> umawiania
      spotkań — propozycje w aplikacji dają wszystkim w grupie równe szanse
      na rozegranie meczu i są warunkiem naliczenia punktów aktywności.
    </p>
    <p>
      <strong>Jak dołączyć:</strong>
    </p>
    <ol className={styles.list}>
      <li>Wejdź w <strong>„Planowanie meczów"</strong> swojej ligi.</li>
      <li>
        Pod tytułem strony znajdziesz zielony przycisk
        {' '}<strong>„💬 Czat grupy {'{Twoja grupa}'}"</strong>:
        <figure className={styles.figure}>
          <img
            src="/whatsapp-group-button.png"
            alt="Przycisk Czat grupy A w widoku Planowania meczów"
            className={styles.figureImage}
            style={{ maxWidth: '180px' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </figure>
        <p className={styles.note} style={{ marginTop: '4px' }}>
          <strong>Koniecznie kliknij w ten przycisk</strong> — to jest właściwe
          dołączenie do czatu Twojej grupy. Bez tego nie będzie Cię na czacie
          i nie otrzymasz informacji przekazywanych przez graczy z Twojej grupy.
        </p>
      </li>
      <li>Klik → otwiera się WhatsApp z prośbą o dołączenie do czatu Twojej grupy.</li>
    </ol>

    <p>
      <strong>Po awansie lub spadku</strong> do innej grupy w nowej rundzie:
    </p>
    <ul className={styles.list}>
      <li>
        Sam wbij się w nowy czat przez przycisk „💬 Czat grupy" w aplikacji
        i <strong>opuść stary ręcznie</strong> w WhatsApp (czat → trzy kropki →
        „Opuść grupę").
      </li>
    </ul>

    <h4 className={styles.subheading}>Kontakt 1-na-1 z graczem z grupy</h4>
    <p>
      W sekcji <strong>„Aktywność w grupie"</strong> przy nazwiskach graczy
      z Twojej grupy pojawia się ikonka <strong>💬</strong> — klik otwiera czat
      WhatsApp <em>1-na-1</em> z tym graczem. Warunki:
    </p>
    <ul className={styles.list}>
      <li>Gracz uzupełnił numer telefonu w <strong>/Dane/Profil → WhatsApp</strong>.</li>
      <li>Zaznaczył checkbox <strong>„whatsapp"</strong> przy danych kontaktowych (zgoda na pokazywanie numeru graczom z grupy).</li>
    </ul>
    <figure className={styles.figure}>
      <img
        src="/whatsapp-profile-settings.jpg"
        alt="Pole numer WhatsApp i checkbox whatsapp w profilu"
        className={`${styles.figureImage} ${styles.figureImageSmallDesktop}`}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
      <figcaption className={styles.figureCaption}>
        Pole „WhatsApp" w profilu (numer w formacie +48 …) oraz checkbox „whatsapp"
        w sekcji „Preferowany kontakt".
      </figcaption>
    </figure>
    <p className={styles.note}>
      Żeby do <strong>Ciebie</strong> mogli pisać inni, zrób to samo w swoim profilu.
      Przy własnym nazwisku ikonki nie zobaczysz — czat sam ze sobą nie ma sensu.
    </p>

    <h4 className={styles.subheading}>Czat to uzupełnienie, nie zastępstwo</h4>
    <p>
      Czat WhatsApp nie zastępuje formalnego planowania:
    </p>
    <ul className={styles.list}>
      <li>
        <strong>Punkty aktywności</strong> naliczają się tylko za działania w systemie
        „Planowanie meczów": rozegrane mecze (1 pkt każdy, bez limitu) oraz wystawione
        propozycje — w tym <strong>te, które nie zostały przyjęte</strong> (max 4 pkt/tydzień,
        zasada „koniec okna od 19:00"). Ustalenia wyłącznie przez czat WhatsApp, bez przejścia
        przez aplikację, nie wliczają się.
      </li>
      <li>
        Rezerwacja kortu (status <strong>LIG</strong>, bez opłaty) tworzy się automatycznie
        po akceptacji propozycji — czat sam tego nie zrobi.
      </li>
      <li>
        Czat służy do umówienia się na ofertę: „dodam za chwilę propozycję 17:00–20:00,
        akceptuj" — następnie obaj wracacie do aplikacji BTT i klikacie.
      </li>
    </ul>
    <p className={styles.note}>
      <strong>
        Uwaga dla graczy niebędących członkami BTT: rezerwujcie kort koniecznie
        przez „Planowanie meczów". Rezerwacja meczu ligowego utworzona w ten
        sposób jest zwolniona z normalnej opłaty za kort, która zgodnie z cennikiem
        obowiązuje graczy spoza członków klubu. Każdy mecz umówiony „na boku"
        bez przejścia przez system spowoduje naliczenie standardowej opłaty.
      </strong>
    </p>

    <h4 className={styles.subheading}>Jeden mecz dziennie</h4>
    <p>
      Po przyjęciu propozycji system automatycznie anuluje wszystkie pozostałe
      aktywne oferty <strong>obu graczy</strong> na ten sam dzień.
      Nie trzeba ręcznie sprzątać — jeśli masz kilka ofert na ten sam dzień i jedna
      zostaje zaakceptowana, reszta znika sama.
    </p>

    <h4 className={styles.subheading}>Ponowna akceptacja od tego samego gracza</h4>
    <p>
      Nie możesz przyjąć nowej oferty od gracza, z którym masz już zaakceptowany
      i <strong>jeszcze nierozpoczęty termin meczu</strong>. Przycisk „Przyjmij"
      dla jego propozycji nie będzie widoczny.
    </p>
    <p>
      Blokada odpada automatycznie w jednym z dwóch przypadków:
    </p>
    <ul className={styles.list}>
      <li>
        <strong>Termin minął</strong> — godzina zakończenia zaakceptowanego meczu
        już upłynęła (niezależnie od tego, czy mecz został rozegrany).
      </li>
      <li>
        <strong>Mecz anulowany</strong> — zaakceptowana propozycja została anulowana
        przez któregoś z graczy.
      </li>
    </ul>
    <p className={styles.note}>
      Jeśli chcesz przełożyć mecz na inny termin, wystarczy anulować istniejącą
      umowę (przycisk „Anuluj" w sekcji „Moje propozycje"), a następnie przyjąć
      nową ofertę od tego gracza.
    </p>

    <h4 className={styles.subheading}>Gdy wynik meczu został już wpisany</h4>
    <p>
      Jeśli wynik Waszego meczu w bieżącej rundzie został wpisany,
      blokada staje się <strong>trwała do końca rundy</strong> — nie możesz ponownie
      przyjąć oferty od tego samego gracza. W systemie Bergera każda para rozgrywa
      dokładnie jeden mecz na rundę, więc ponowne umawianie się nie jest potrzebne.
    </p>
    <p className={styles.note}>
      Blokada dotyczy bieżącej rundy. W kolejnej rundzie — jeśli trafisz ponownie
      na tego samego rywala — możesz przyjmować jego oferty normalnie.
    </p>

    <h4 className={styles.subheading}>Jak długo widać propozycje?</h4>
    <p>
      Propozycje znikają z widoku automatycznie:
    </p>
    <table className={styles.table}>
      <thead>
        <tr><th>Status</th><th>Znika po</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Aktywna, Zaakceptowana, Wygasła</td>
          <td>3 dni od daty meczu</td>
        </tr>
        <tr>
          <td>Anulowana</td>
          <td>3 dni od daty anulowania</td>
        </tr>
      </tbody>
    </table>
    <p className={styles.note}>
      Propozycje znikają z widoku, ale pozostają w bazie danych systemu.
    </p>

    <h4 className={styles.subheading}>Kiedy składać propozycję?</h4>
    <p>
      Propozycji nie można złożyć na przeszłą datę. Technicznie można ją złożyć
      nawet na kilka godzin przed meczem — ale taka sytuacja jest zarezerwowana dla przypadków,
      gdy gracze są już praktycznie umówieni i rezerwacja to tylko formalność.
    </p>

    <div className={styles.example}>
      <strong>Zalecana praktyka:</strong> zamiast kilku osobnych ofert na konkretne godziny,
      wystaw jedną propozycję z szerokim oknem — np. 16:00–20:00.
      Rywal sam wybierze slot, który mu odpowiada. Składaj oferty z co najmniej jednodniowym
      wyprzedzeniem — tak, żeby inni gracze w grupie zdążyli zareagować.
      Im więcej <em>różnych dni</em> obstawisz ofertami, tym większa szansa na grę — w jednym dniu
      i tak zmieszczą się maksymalnie 2 aktywne oferty, i to bez zachodzenia na siebie.
    </div>
  </div>
);

const FreezeContent = () => (
  <div className={styles.drawerContent}>
    <p>
      Możesz zamrozić swój udział na wybraną rundę — na przykład gdy wyjeżdżasz
      lub masz kontuzję. To elastyczność, która nie wyklucza Cię z ligi.
    </p>

    <h4 className={styles.subheading}>Zasady zamrożenia</h4>
    <ul className={styles.list}>
      <li>Zamrożenie trwa <strong>całą rundę</strong></li>
      <li>Możesz zamrozić się <strong>maksymalnie 2 razy w sezonie</strong></li>
      <li>Zamrożenie zgłaszasz <strong>przed startem kolejnej rundy</strong> — najwcześniej od rundy 2</li>
      <li>W zamrożonej rundzie nie są dla Ciebie generowane mecze</li>
    </ul>

    <h4 className={styles.subheading}>Punkty za zamrożoną rundę</h4>
    <p>
      Za zamrożoną rundę otrzymujesz <strong>rekompensatę 80%</strong> — wyliczaną jako
      średnia arytmetyczna punktów z rund rozegranych <strong>przed</strong> zamrożeniem,
      pomnożona przez 0,8. Podstawą jest Twój dotychczasowy dorobek w momencie zamrożenia.
      Dzięki temu zamrożenie nie wyklucza Cię z rankingu ogólnego.
    </p>
    <div className={styles.example}>
      <strong>Przykład:</strong><br />
      Marek rozegrał rundy 1 i 2 zdobywając 8 i 6 pkt, zamrożony w rundzie 3.<br />
      Średnia z rund przed zamrożeniem: (8 + 6) / 2 = 7,0 pkt<br />
      Rekompensata za rundę 3: 7,0 × 80% = <strong>5,6 pkt</strong>
    </div>

    <h4 className={styles.subheading}>Co się NIE zmienia przy zamrożeniu?</h4>
    <ul className={styles.list}>
      <li><strong>Seria zwycięstw</strong> — nie zostaje przerwana; kontynuujesz ją po powrocie</li>
      <li>Twoja historia wyników i pozycja w historii ligi</li>
    </ul>

    <h4 className={styles.subheading}>Wpływ zamrożeń na transfery między grupami</h4>
    <p>
      Gdy w grupie kilku graczy zamrozi rundę, mniej osób bierze udział w awansach
      i spadkach — tak żeby rozmiary grup zostały zachowane (np. wszystkie 5-osobowe).
      Liczba awansów i spadków „skaluje się w dół" symetrycznie z grupami sąsiednimi.
    </p>
    <p className={styles.note}>
      W praktyce: domyślnie awansuje 2 i spada 2. Jeśli grupa ma 2 lub 3 zamrożonych
      (zostaje 3 lub 2 aktywnych), awansuje 1 i spada 1 — w tej grupie oraz w obu
      grupach sąsiednich. Kolory w tabeli (<span style={{ color: '#2e7d32' }}>zielony</span>
      = awans, <span style={{ color: '#c62828' }}>czerwony</span> = spadek) na bieżąco
      pokazują rzeczywistą, zredukowaną liczbę miejsc — nie statyczny parametr ligi.
    </p>

    <h4 className={styles.subheading}>Gdy w grupie zostaje tylko 1 aktywny gracz</h4>
    <p>
      Skrajny przypadek: jeśli w grupie 4 osoby zamrożą rundę, a aktywny zostaje
      tylko jeden gracz, system <strong>automatycznie zamraża również jego</strong> —
      nie ma z kim grać. To zamrożenie <strong>nie zużywa</strong> limitu 2 zamrożeń
      na sezon (to nie Twój wybór, lecz konsekwencja decyzji innych w grupie).
    </p>
    <p className={styles.note}>
      W takiej rundzie cała grupa pauzuje — brak transferów do/z grup sąsiednich.
      W kolejnej rundzie wszyscy automatycznie wracają do gry.
    </p>

    <h4 className={styles.subheading}>Zasada jednego przeskoku przy spauzowanych grupach</h4>
    <p>
      Gdy sąsiednia grupa jest spauzowana (cała zamrożona), awans lub spadek może
      <strong> przeskoczyć ją o jedną grupę</strong>. Na przykład: gdy grupa D pauzuje,
      gracz z grupy E może awansować bezpośrednio do C (a gracz z C może spaść do E).
    </p>
    <p className={styles.note}>
      <strong>Maksymalnie jeden przeskok.</strong> Gracz z grupy E nie może awansować
      dalej niż do C, nawet jeśli zarówno C jak i D są spauzowane. W takim przypadku
      ruch jest wstrzymany — gracz zostaje w swojej grupie do następnej rundy.
    </p>
  </div>
);

const AwardsContent = () => (
  <div className={styles.drawerContent}>
    <p>
      Obok głównego rankingu ligowego istnieje 9 przekrojowych kategorii wyróżnień —
      widocznych dla wszystkich graczy, niezależnie od grupy.
      Nie musisz wygrać ligi, aby zostać zauważonym!
    </p>

    <table className={styles.table}>
      <thead>
        <tr><th>Wyróżnienie</th><th>Za co?</th></tr>
      </thead>
      <tbody>
        <tr><td>🏆 Najwięcej wygranych</td><td>Łączna liczba wygranych meczów</td></tr>
        <tr><td>🔥 Najdłuższa seria</td><td>Najdłuższa passa zwycięstw z rzędu</td></tr>
        <tr><td>📊 Bilans gemów</td><td>Różnica gemów wygranych i przegranych</td></tr>
        <tr><td>🎯 Najwyższy % wygranych</td><td>Procent wygranych meczów (min. 3 mecze)</td></tr>
        <tr><td>📈 Bilans setów</td><td>Różnica setów wygranych i przegranych</td></tr>
        <tr><td>⚡ Wygrane tiebreaki</td><td>Liczba wygranych tiebreaków</td></tr>
        <tr><td>💪 Najbardziej dominujący</td><td>Liczba wygranych 2:0 w setach (bez straty seta)</td></tr>
        <tr><td>👑 Król powrotów</td><td>Wygrane po przegraniu pierwszego seta</td></tr>
        <tr><td>⚔️ Wojownik</td><td>Największa liczba rozegranych tiebreaków</td></tr>
      </tbody>
    </table>

    <p className={styles.note}>
      W każdej kategorii wyróżniane są 3 pierwsze miejsca (🥇🥈🥉).
      Walkowery nie są wliczane (mecz w ogóle się nie odbył). Mecze przerwane
      z powodu kontuzji <strong>są wliczane</strong> — wynik jest uzupełniany
      do pełnych setów. Gemy rozegrane przed kontuzją zostają zachowane, pozostałe
      zaliczane są na korzyść aktywnego gracza. Przykład: przy stanie 4:2 w I secie
      kontuzję odnosi prowadzący — końcowy wynik to <strong>4:6, 0:6</strong>.
    </p>
    <p className={styles.note}>
      W kategoriach <strong>⚡ Wygrane tiebreaki</strong> i <strong>⚔️ Wojownik</strong>
      liczone są zarówno tiebreaki rozgrywane w obrębie seta, jak i super-tiebreaki
      rozgrywane jako trzeci, decydujący set meczu.
    </p>
  </div>
);

const TiebreakerContent = () => (
  <div className={styles.drawerContent}>
    <p>
      Gdy gracze mają tę samą liczbę punktów w <strong>tabeli grupowej</strong>, kolejność
      wyznaczana jest według poniższych kryteriów — po kolei, aż do rozstrzygnięcia.
      Zasady różnią się w zależności od liczby remisujących graczy.
      Rozstrzyganie remisów w rankingu ogólnym opisane jest w zakładce <strong>Ranking ogólny</strong>.
    </p>

    <h4 className={styles.subheading}>Remis dwóch graczy</h4>
    <ol className={styles.list}>
      <li><strong>Wynik bezpośredni</strong> — kto wygrał mecz między nimi?</li>
      <li><strong>Bilans setów (H2H)</strong> — sety w bezpośrednim meczu</li>
      <li><strong>Bilans gemów (H2H)</strong> — gemy w bezpośrednim meczu</li>
      <li><strong>Bilans setów (globalne)</strong> — ze wszystkich meczów w grupie w rundzie</li>
      <li><strong>Bilans gemów (globalne)</strong> — ze wszystkich meczów w grupie w rundzie</li>
      <li><strong>Rozstrzygnięcie systemowe (pseudo-losowe)</strong> — wynik wyliczany ze stałych danych, zawsze identyczny</li>
    </ol>

    <div className={styles.example}>
      <strong>Przykład:</strong><br />
      Jan i Piotr mają po 6 punktów. Jan wygrał ich bezpośredni mecz 2:1 →
      Jan zajmuje wyższe miejsce.
    </div>

    <h4 className={styles.subheading}>Remis trzech lub więcej graczy</h4>
    <p>
      Tworzy się mini-ligę spośród remisujących graczy i analizuje wyłącznie
      mecze między nimi:
    </p>
    <ol className={styles.list}>
      <li><strong>Bilans setów (mini-liga)</strong> — sety z meczów między remisującymi</li>
      <li><strong>Bilans gemów (mini-liga)</strong> — gemy z meczów między remisującymi</li>
      <li><strong>Bilans setów (globalne)</strong> — ze wszystkich meczów w grupie w rundzie</li>
      <li><strong>Bilans gemów (globalne)</strong> — ze wszystkich meczów w grupie w rundzie</li>
      <li><strong>Rozstrzygnięcie systemowe (pseudo-losowe)</strong></li>
    </ol>

    <p className={styles.note}>
      W remisie 3+ graczy nie sprawdza się kto kogo pokonał bezpośrednio —
      liczy się łączny bilans setów i gemów z meczów w mini-lidze.
    </p>
  </div>
);

// ─── Sections config ──────────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: 'groups',
    icon: '🏆',
    title: 'Grupy i awanse',
    summary: 'Grupy A–E według poziomu gry, premia bazowa i awanse — liczba awansujących może się zmieniać w trakcie sezonu.',
    Content: GroupsContent,
  },
  {
    id: 'scoring',
    icon: '📊',
    title: 'System punktacji',
    summary: 'System 3-2-1-0, średnia punktów za mecz do rankingu, premia grupowa i bonus top 3.',
    Content: ScoringContent,
  },
  {
    id: 'ranking',
    icon: '📈',
    title: 'Ranking ogólny',
    summary: 'Aktualizowany na bieżąco po każdym meczu. Awans do playoff dla 12 najlepszych oraz zasady rozstrzygania remisów.',
    Content: RankingContent,
  },
  {
    id: 'activity',
    icon: '⚡',
    title: 'Punkty za aktywność',
    summary: 'Decydują o walkowerze przy nierozegranych meczach — gracz z większą aktywnością wygrywa.',
    Content: ActivityContent,
  },
  {
    id: 'planning',
    icon: '📅',
    title: 'Umawianie spotkań',
    summary: 'Formularz planowania meczów — rezerwacja kortu LIG bez opłat, jeden mecz dziennie, widoczność ofert, powiadomienia e-mail.',
    Content: MatchPlanningContent,
  },
  {
    id: 'freeze',
    icon: '❄️',
    title: 'Zamrożenie rundy',
    summary: 'Zawieś udział na wybraną rundę — do 2 razy w sezonie. Za zamrożoną rundę rekompensata 80%.',
    Content: FreezeContent,
  },
  {
    id: 'awards',
    icon: '🥇',
    title: 'Wyróżnienia ligowe',
    summary: '9 kategorii dla całej ligi — nie musisz wygrać ligi, aby zostać zauważonym.',
    Content: AwardsContent,
  },
  {
    id: 'tiebreak',
    icon: '⚖️',
    title: 'Remisy — rozstrzyganie',
    summary: 'Osobne zasady dla remisu dwóch graczy i dla trzech lub więcej — tabela grupowa.',
    Content: TiebreakerContent,
  },
];

// ─── Main component ───────────────────────────────────────────────────────────

const LeagueInfo = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [activityHours, setActivityHours] = useState(36);
  useEffect(() => {
    fetchActivityCreditHours().then((h) => {
      if (typeof h === 'number') setActivityHours(h);
    });
  }, []);

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>Liga Grupowa — szczegółowy opis</h1>
        <p className={styles.heroSubtitle}>
          Kliknij sekcję, żeby dowiedzieć się więcej.
        </p>
      </section>

      <section className={styles.gridSection}>
        <div className={styles.grid}>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              className={styles.card}
              onClick={() => setActiveSection(section)}
            >
              <span className={styles.cardIcon}>{section.icon}</span>
              <h3 className={styles.cardTitle}>{section.title}</h3>
              <p className={styles.cardSummary}>{section.summary}</p>
            </button>
          ))}
        </div>
      </section>

      <Drawer
        open={!!activeSection}
        onClose={() => setActiveSection(null)}
        placement="bottom"
        height="80vh"
        closable={false}
        title={null}
        styles={{ body: { padding: '16px 20px 32px' } }}
      >
        <div className={styles.drawerInner}>
          <div className={styles.drawerHeader}>
            <button className={styles.drawerClose} onClick={() => setActiveSection(null)}>✕</button>
            <span className={styles.drawerTitle}>{activeSection?.title}</span>
          </div>
          {activeSection && <activeSection.Content activityHours={activityHours} />}
        </div>
      </Drawer>
    </div>
  );
};

export default LeagueInfo;
